import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Add axios interceptor to include auth token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Invoice API calls
export const invoiceApi = {
  getInvoices: async (page = 1, pageSize = 10, search = '', status = 'all') => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    
    if (search) params.append('search', search);
    if (status && status !== 'all') params.append('status', status);
    
    const response = await axios.get(`${API}/invoices?${params.toString()}`);
    return response.data;
  },

  getStats: async () => {
    const response = await axios.get(`${API}/invoices/stats`);
    return response.data;
  },

  getInvoice: async (id) => {
    const response = await axios.get(`${API}/invoices/${id}`);
    return response.data;
  },

  createInvoice: async (invoiceData = null) => {
    const response = await axios.post(`${API}/invoices`, invoiceData);
    return response.data;
  },

  updateInvoice: async (id, invoiceData) => {
    const response = await axios.put(`${API}/invoices/${id}`, invoiceData);
    return response.data;
  },

  deleteInvoice: async (id) => {
    const response = await axios.delete(`${API}/invoices/${id}`);
    return response.data;
  },

  seedInvoices: async () => {
    const response = await axios.post(`${API}/invoices/seed`);
    return response.data;
  }
};

// Rental Agreement API calls
export const agreementApi = {
  getAgreements: async (page = 1, pageSize = 10, search = '', status = 'all') => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    
    if (search) params.append('search', search);
    if (status && status !== 'all') params.append('status', status);
    
    const response = await axios.get(`${API}/agreements?${params.toString()}`);
    return response.data;
  },

  getStats: async () => {
    const response = await axios.get(`${API}/agreements/stats`);
    return response.data;
  },

  getAgreement: async (id) => {
    const response = await axios.get(`${API}/agreements/${id}`);
    return response.data;
  },

  createAgreement: async (agreementData = null) => {
    const response = await axios.post(`${API}/agreements`, agreementData);
    return response.data;
  },

  updateAgreement: async (id, agreementData) => {
    const response = await axios.put(`${API}/agreements/${id}`, agreementData);
    return response.data;
  },

  deleteAgreement: async (id) => {
    const response = await axios.delete(`${API}/agreements/${id}`);
    return response.data;
  },

  seedAgreements: async () => {
    const response = await axios.post(`${API}/agreements/seed`);
    return response.data;
  }
};

// Fleet Vehicle API calls
export const vehicleApi = {
  getVehicles: async (page = 1, pageSize = 10, search = '', status = 'all', category = 'all') => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    
    if (search) params.append('search', search);
    if (status && status !== 'all') params.append('status', status);
    if (category && category !== 'all') params.append('category', category);
    
    const response = await axios.get(`${API}/vehicles?${params.toString()}`);
    return response.data;
  },

  getAllVehicles: async () => {
    const response = await axios.get(`${API}/vehicles/all`);
    return response.data;
  },

  getStats: async () => {
    const response = await axios.get(`${API}/vehicles/stats`);
    return response.data;
  },

  getVehicle: async (id) => {
    const response = await axios.get(`${API}/vehicles/${id}`);
    return response.data;
  },

  createVehicle: async (vehicleData = null) => {
    const response = await axios.post(`${API}/vehicles`, vehicleData);
    return response.data;
  },

  updateVehicle: async (id, vehicleData) => {
    const response = await axios.put(`${API}/vehicles/${id}`, vehicleData);
    return response.data;
  },

  deleteVehicle: async (id) => {
    const response = await axios.delete(`${API}/vehicles/${id}`);
    return response.data;
  },

  seedVehicles: async () => {
    const response = await axios.post(`${API}/vehicles/seed`);
    return response.data;
  }
};

// Email API calls
export const emailApi = {
  sendInvoice: async (invoiceId, emailData) => {
    const response = await axios.post(`${API}/email/invoice/${invoiceId}`, emailData);
    return response.data;
  },

  sendAgreement: async (agreementId, emailData) => {
    const response = await axios.post(`${API}/email/agreement/${agreementId}`, emailData);
    return response.data;
  },

  sendTestEmail: async (emailData) => {
    const response = await axios.post(`${API}/email/test`, emailData);
    return response.data;
  }
};

// Upload API calls
export const uploadApi = {
  uploadVehicleImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API}/uploads/vehicle-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  deleteVehicleImage: async (filename) => {
    const response = await axios.delete(`${API}/uploads/vehicles/${filename}`);
    return response.data;
  },

  getVehicleImageUrl: (filename) => {
    if (!filename) return null;
    // If it's already a full URL, return as-is
    if (filename.startsWith('http')) return filename;
    // If it starts with /api, use backend URL
    if (filename.startsWith('/api')) return `${BACKEND_URL}${filename}`;
    // Otherwise, construct the URL
    return `${BACKEND_URL}/api/uploads/vehicles/${filename}`;
  }
};

// Dashboard API calls
export const dashboardApi = {
  getStats: async () => {
    const response = await axios.get(`${API}/dashboard/stats`);
    return response.data;
  }
};

export default { invoiceApi, agreementApi, vehicleApi, emailApi, uploadApi, dashboardApi };
